import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightFromBracket,
  faGear,
  faIdCardClip,
  faUserCircle,
  faUsers,
} from '@fortawesome/pro-solid-svg-icons';
import Dropdown from '@mysidewalk/react-dropdown';
import UserAvatar from '@mysidewalk/react-useravatar';
import Tag, { TagVariant } from '@mysidewalk/react-tag';

import {
  AUTHENTICATION_SIGN_IN,
  AUTHENTICATION_SIGN_OUT,
  USER_ORGANIZATION_INDEX,
  USER_ORGANIZATION_SHOW,
  USER_PREFERENCES,
} from './URLs.js';

import Access from './types/Access';
import Domains from './types/Domains';
import LinkHandler from './types/LinkHandler';
import User from './types/User';

export type Props = {
  user?: User;
  access: Access;
  domains: Domains;
  onClickLink: LinkHandler;
};

function UserMenu({
  user,
  access,
  domains,
  onClickLink,
}: Props) {
  if (!access.hasAuthenticatedAccess || !user) {
    return (
      <a
        id="mysw-header-sign-in"
        data-test-id="header.user-menu.links.sign-in"
        href={`${domains.chart}${AUTHENTICATION_SIGN_IN}`}
        onClick={onClickLink}
        className="ApplicationHeader-action"
        style={{ display: 'inline-flex', alignItems: 'center', padding: '0 var(--mysw-spacer-2)', fontSize: 'var(--mysw-size-600)' }}
      >
        <FontAwesomeIcon
          icon={faUserCircle}
          className="fa-fw"
          style={{ fontSize: 'var(--mysw-size-400)', color: 'white', marginRight: '6px' }}
        />
        Sign in
      </a>
    );
  }

  const staffItems = (
    access.hasStaffAccess
      ? (
        <Dropdown.Item
          id="user-menu-change-organization"
          data-test-id="header.user-menu.links.organization-change"
          href={`${domains.chart}${USER_ORGANIZATION_INDEX}`}
          onClick={onClickLink}
        >
          <Dropdown.ItemIcon><FontAwesomeIcon className="fa-fw" icon={faIdCardClip} /></Dropdown.ItemIcon>
          Change organization
          <Tag variant={TagVariant.subtle} style={{ marginLeft: 'var(--mysw-spacer-2)' }}>Staff</Tag>
        </Dropdown.Item>
      )
      : null
  );

  return (
    <Dropdown variant="primary" className="ApplicationHeader-Dropdown">
      <Dropdown.Toggle
        className="ApplicationHeader-action"
        id="mysw-header-user-menu-toggle"
        title="View account options"
      >
        <UserAvatar user={user} />
        <span className="sr-only">View user &amp; authentication options</span>
      </Dropdown.Toggle>
      <Dropdown.Menu align="right">
        <Dropdown.Item
          id="user-menu-preferences"
          data-test-id="header.user-menu.links.preferences"
          href={`${domains.seek}${USER_PREFERENCES}`}
          onClick={onClickLink}
        >
          <Dropdown.ItemIcon><FontAwesomeIcon className="fa-fw" icon={faGear} /></Dropdown.ItemIcon>
          Your preferences
        </Dropdown.Item>
        <Dropdown.Item
          id="user-menu-organization"
          data-test-id="header.user-menu.links.organization"
          href={`${domains.seek}${USER_ORGANIZATION_SHOW}`}
          onClick={onClickLink}
        >
          <Dropdown.ItemIcon><FontAwesomeIcon className="fa-fw" icon={faUsers} /></Dropdown.ItemIcon>
          Your organization
        </Dropdown.Item>
        {staffItems}
        <Dropdown.Item
          id="user-menu-sign-out"
          data-test-id="header.user-menu.links.sign-out"
          href={`${domains.signOut ?? domains.chart}${AUTHENTICATION_SIGN_OUT}`}
          onClick={onClickLink}
        >
          <Dropdown.ItemIcon><FontAwesomeIcon className="fa-fw" icon={faArrowRightFromBracket} /></Dropdown.ItemIcon>
          Sign out
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}

UserMenu.displayName = 'UserMenu';

export default UserMenu;
